import { ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type AscImportMode,
  type AscImportReport,
  type AscManualMapping,
  type AscUnresolvedEntity,
  uploadAscTimetableImport,
} from '../../../utils/timetable/timetableVersion';
import { showSuccessToast } from '../../../utils/toast';
import { AscImportReportModal } from '../modals/AscImportReportModal';

/**
 * Der aSc-Import ergänzt die Version nur. Den Modus „fresh" (alles entfernen, was die Datei nicht
 * enthält) kann die Route weiterhin, das FE bietet ihn aber bewusst noch nicht an — er löscht
 * Karten und Unterrichte, ohne dass die Vorschau das ausreichend deutlich macht.
 */
const ASC_IMPORT_MODE: AscImportMode = 'keep';

type UseAscImportOptions = {
  /** Version, gegen die importiert wird. Ohne UUID passiert nichts. */
  versionUuid: string | undefined;
  /**
   * Nach erfolgreichem Import. Der Import läuft per REST, urql bekommt davon nichts mit — der
   * Aufrufer muss seine Daten selbst neu laden, sonst zeigt er gelöschte Unterrichte weiter an.
   */
  onImported?: (report: AscImportReport) => void;
};

type UseAscImportResult = {
  /** Öffnet den Datei-Dialog und startet damit den Trockenlauf. */
  pickAscFile: () => void;
  /** Solange eine Anfrage läuft — für Spinner und deaktivierte Menüeinträge. */
  ascBusy: boolean;
  /**
   * Dialog samt verborgenem Datei-Input. Als Element und nicht als Komponente, weil eine im Hook
   * deklarierte Komponente bei jedem Render einen neuen Typ hätte: React würde den Baum komplett
   * neu mounten und der Assistent bei jedem Re-Render auf die erste Folie zurückspringen.
   */
  ascImportDialog: ReactNode;
};

/**
 * Kompletter aSc-Import: Datei wählen → Vorschau (Trockenlauf) → Zuordnungen → Import.
 *
 * Der Aufrufer braucht davon nur `pickAscFile()` an einen Menüeintrag zu hängen und
 * `ascImportDialog` zu rendern.
 */
export function useAscImport({ versionUuid, onImported }: UseAscImportOptions): UseAscImportResult {
  const { t } = useTranslation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Die Datei muss zwischen Vorschau und Bestätigung erhalten bleiben — der Import schickt sie
  // ein zweites Mal, diesmal an die schreibende Route.
  const fileRef = useRef<File | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [report, setReport] = useState<{ report: AscImportReport; variant: 'preview' | 'result' } | null>(null);
  const [runId, setRunId] = useState(0);
  // Zuordnungen des Anwenders, Schlüssel `<kind>:<ascId>`. `null` = überspringen.
  const [mappings, setMappings] = useState<Record<string, string | null>>({});

  // Stabile Referenz: die Zuordnungs-Zeilen sind memoisiert, ein neuer Handler je Render würde
  // die Memoisierung aushebeln.
  const onMappingChange = useCallback((entry: AscUnresolvedEntity, targetUuid: string | null | undefined) => {
    setMappings((current) => {
      const key = `${entry.kind}:${entry.ascId}`;
      if (targetUuid === undefined) {
        const { [key]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [key]: targetUuid };
    });
  }, []);

  const mappingList = (): AscManualMapping[] =>
    Object.entries(mappings).map(([key, targetUuid]) => {
      const [kind, ...rest] = key.split(':');
      return { kind: kind as AscManualMapping['kind'], ascId: rest.join(':'), targetUuid };
    });

  function pickAscFile() {
    setMappings({});
    // Neue Datei = neuer Lauf: der Assistent bekommt einen frischen Key, damit er wieder bei der
    // ersten Folie startet statt dort, wo der letzte Lauf aufgehört hat.
    setRunId((id) => id + 1);
    fileInputRef.current?.click();
  }

  /** Schritt 1: Trockenlauf. Es wird noch nichts geschrieben — der Anwender bestätigt erst. */
  async function onFileSelected(file: File) {
    if (!versionUuid) return;

    fileRef.current = file;
    setBusy(true);
    try {
      const result = await uploadAscTimetableImport(versionUuid, file, ASC_IMPORT_MODE, { preview: true });
      if (result) {
        setReport({ report: result, variant: 'preview' });
      }
    } finally {
      setBusy(false);
    }
  }

  /** Vorschau erneut rechnen — zeigt sofort, was die Zuordnungen bringen. */
  async function recheck() {
    const file = fileRef.current;
    if (!versionUuid || !file) return;

    setBusy(true);
    try {
      const result = await uploadAscTimetableImport(versionUuid, file, ASC_IMPORT_MODE, {
        preview: true,
        mappings: mappingList(),
      });
      if (result) {
        setReport({ report: result, variant: 'preview' });
      }
    } finally {
      setBusy(false);
    }
  }

  /** Schritt 2: nach Bestätigung derselbe Abgleich, jetzt schreibend. */
  async function confirm() {
    const file = fileRef.current;
    if (!versionUuid || !file) return;

    setBusy(true);
    try {
      const result = await uploadAscTimetableImport(versionUuid, file, ASC_IMPORT_MODE, {
        mappings: mappingList(),
      });
      if (!result) return;

      onImported?.(result);

      // Nicht auflösbare Entitäten sind kein Fehler — der Rest wurde importiert. Nur wenn es
      // nichts zu berichten gibt, reicht ein Toast.
      if (result.unresolved.length > 0 || result.warnings.length > 0) {
        setReport({ report: result, variant: 'result' });
        return;
      }
      setReport(null);
      showSuccessToast(
        t('pinboard.actions.ascImportResult', { imported: result.cardsImported, total: result.cardsInFile }),
      );
    } finally {
      setBusy(false);
    }
  }

  const ascImportDialog = (
    <>
      <AscImportReportModal
        key={runId}
        report={report?.report ?? null}
        variant={report?.variant ?? 'preview'}
        busy={busy}
        mappings={mappings}
        onMappingChange={onMappingChange}
        onRecheck={() => void recheck()}
        onConfirm={() => void confirm()}
        onClose={() => setReport(null)}
      />
      <input
        ref={fileInputRef}
        type='file'
        accept='.xml,application/xml,text/xml'
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Zurücksetzen, damit dieselbe Datei direkt noch einmal gewählt werden kann.
          e.target.value = '';
          if (file) {
            void onFileSelected(file);
          }
        }}
      />
    </>
  );

  return { pickAscFile, ascBusy: busy, ascImportDialog };
}
